import type { JobOpeningStatus, StageBar, StageBarKey, StageBarStage } from '@nest-recruit/shared';

// JOB-01's Stages column as a progress track (owner's pick B, DECISIONS-LOG 2026-10-27;
// Docs/Mockups/job-list-stages-ideas/jobs-b.html). One dot per stage, always in the same
// place on every row, then the stages that hold people in words. Built from the same
// StageBar as the job page's bar (§L33), so the two never disagree.

// ADR 0079: Screening (the optional phone step) is a speck on every job that hasn't switched it on, as Assessment is.
export const TRACK_SLOT_KEYS = ['import', 'ranking', 'review', 'shortlist', 'phone_screen', 'contact', 'assessment', 'interview', 'selected'] as const;
export type TrackSlotKey = (typeof TRACK_SLOT_KEYS)[number];

/**
 * passed: the job has moved past it, now: where the job is (§L33's current stage), here: a
 * later stage some people have already reached, ahead: still to come, unused: the job
 * doesn't use it (no test, no interview rounds) — a speck, so the other dots never shift.
 */
export type TrackDot = 'passed' | 'now' | 'here' | 'ahead' | 'unused';

export interface TrackRound {
  readonly key: StageBarKey;
  readonly label: string;
  readonly count: number;
  readonly dot: TrackDot;
}

export interface TrackSlot {
  readonly key: TrackSlotKey;
  readonly label: string;
  readonly count: number;
  readonly dot: TrackDot;
  /** Interview only: one entry per round the job uses, for the split on pointing and the pop-up. */
  readonly rounds: readonly TrackRound[];
}

export interface TrackWord {
  readonly label: string;
  readonly count: number;
}

export interface StageTrack {
  readonly slots: readonly TrackSlot[];
  /** Where the line's colour stops: the "now" dot's position, or 0 when there is none. */
  readonly fillTo: number;
  /** The stages holding people, in hiring order, at most WORDS_SHOWN; the first is the current one. */
  readonly words: readonly TrackWord[];
  /** How many more stages hold people than the words show ("+1"). */
  readonly more: number;
  /** What the words say when nobody is in any stage. Null when someone is. */
  readonly emptyText: string | null;
  /** Filled, closed and cancelled jobs fade, so live jobs stand out. */
  readonly isEnded: boolean;
}

/** The current stage, then up to two others (the drawing's "Review 56 · Shortlist 4 · Contact 1"). */
const WORDS_SHOWN = 3;

const SLOT_LABELS: Readonly<Record<TrackSlotKey, string>> = {
  import: 'Import',
  ranking: 'Automated ranking',
  review: 'Review',
  shortlist: 'Shortlist',
  phone_screen: 'Screening',
  contact: 'Contact',
  assessment: 'Assessment',
  interview: 'Interview',
  selected: 'Selected',
};

const INTERVIEW_KEYS: readonly StageBarKey[] = ['interview_1', 'interview_2', 'interview_3'];

const ENDED_STATUSES: readonly JobOpeningStatus[] = ['filled', 'closed_partial', 'cancelled'];

export const EMPTY_TEXT = {
  draft: 'Setting up',
  ended: 'No one in progress',
  open: 'No one yet',
} as const;

function dotOf(stage: StageBarStage): TrackDot {
  const count = stage.count ?? 0;
  if (stage.state === 'current') return 'now';
  if (stage.state === 'done') return 'passed';
  return count > 0 ? 'here' : 'ahead';
}

/** All rounds count as one stage: now if one is, passed when all are, here if any holds people. */
function interviewSlot(rounds: readonly StageBarStage[]): TrackSlot {
  const parts = rounds.map((round): TrackRound => ({ key: round.key, label: round.label, count: round.count ?? 0, dot: dotOf(round) }));
  const count = parts.reduce((sum, round) => sum + round.count, 0);
  const dot = slotDotOf(parts.map((round) => round.dot), count);
  return { key: 'interview', label: SLOT_LABELS.interview, count, dot, rounds: parts };
}

function slotDotOf(dots: readonly TrackDot[], count: number): TrackDot {
  if (dots.length === 0) return 'unused';
  if (dots.includes('now')) return 'now';
  if (dots.every((dot) => dot === 'passed')) return 'passed';
  return count > 0 ? 'here' : 'ahead';
}

function slotOf(key: TrackSlotKey, bar: StageBar): TrackSlot {
  if (key === 'interview') return interviewSlot(bar.stages.filter((stage) => INTERVIEW_KEYS.includes(stage.key)));
  const stage = bar.stages.find((candidate) => candidate.key === key);
  if (!stage) return { key, label: SLOT_LABELS[key], count: 0, dot: 'unused', rounds: [] };
  return { key, label: SLOT_LABELS[key], count: stage.count ?? 0, dot: dotOf(stage), rounds: [] };
}

function emptyTextOf(status: JobOpeningStatus): string {
  if (status === 'draft') return EMPTY_TEXT.draft;
  if (ENDED_STATUSES.includes(status)) return EMPTY_TEXT.ended;
  return EMPTY_TEXT.open;
}

/**
 * The track for one job. A switched-off module's stage (#13) is left out of every row alike, so
 * the dots still line up down the column. ① Job is left out: the row's own status says that.
 */
export function stageTrackOf(bar: StageBar, status: JobOpeningStatus, hiddenStages: ReadonlySet<StageBarKey>): StageTrack {
  const isHidden = (key: TrackSlotKey): boolean => [...hiddenStages].some((hidden) => hidden === key);
  const slots = TRACK_SLOT_KEYS.filter((key) => !isHidden(key)).map((key) => slotOf(key, bar));
  const nowIndex = slots.findIndex((slot) => slot.dot === 'now');
  const held = slots.filter((slot) => slot.count > 0).map((slot): TrackWord => ({ label: slot.label, count: slot.count }));
  return {
    slots,
    fillTo: Math.max(nowIndex, 0),
    words: held.slice(0, WORDS_SHOWN),
    more: Math.max(held.length - WORDS_SHOWN, 0),
    emptyText: held.length === 0 ? emptyTextOf(status) : null,
    isEnded: ENDED_STATUSES.includes(status),
  };
}

export interface TrackListRow {
  readonly key: StageBarKey;
  readonly label: string;
  readonly count: number;
  readonly dot: TrackDot;
  readonly isRound: boolean;
}

/** The pop-up's list: every stage the job draws, with its count; interview rounds indented under the others. */
export function trackListOf(bar: StageBar, hiddenStages: ReadonlySet<StageBarKey>): readonly TrackListRow[] {
  return bar.stages
    .filter((stage) => stage.key !== 'job' && !hiddenStages.has(stage.key))
    .map((stage) => ({ key: stage.key, label: stage.label, count: stage.count ?? 0, dot: dotOf(stage), isRound: INTERVIEW_KEYS.includes(stage.key) }));
}

/** The export and screen-reader version of the words: "Review 56 · Shortlist 4 · Contact 1 · +1 more". */
export function trackSummaryOf(track: StageTrack): string {
  if (track.emptyText !== null) return track.emptyText;
  const words = track.words.map((word) => `${word.label} ${word.count.toLocaleString('en-US')}`);
  return [...words, ...(track.more > 0 ? [`+${track.more} more`] : [])].join(' · ');
}
